import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  Textarea,
  Dropdown,
  Option,
  Spinner,
  MessageBar,
  MessageBarBody,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { FilingCadence, NameMismatchAck, StateTaxObligationSummary } from '../../types';
import { useRecordManualFiling, uploadManualFilingAttachment, extractManualFiling } from '../../services/stateTaxClientsApi';
import { filedAmounts } from '../../services/displayAmounts';
import DocumentDropSlot from '../upload/DocumentDropSlot';
import { routeDrop, type FilingDocumentKind } from '../upload/uploadRules';
import { NameMismatchWarning } from './NameMismatchWarning';
import { shouldGateManualFilingSave, resolveManualFilingSaveAck, type ManualFilingNameGuard } from '../../utils/nameGuard';
import { CalendarDateInput } from '../dates/CalendarDateInput';

/**
 * Record a filing that happened but no Dataverse task captured — the manual "override". App-owned
 * only (no Dataverse write). Optionally attaches the return PDF and, for obligations whose form
 * arrives with a SEPARATE payment-confirmation report (sales tax, solid waste), a second payment
 * PDF. Surfaced afterward in the recent-history grid + filing-activity feed as "filed (manual)".
 *
 * Each uploaded PDF runs a task-less extraction (POST .../manual-filings/extract) with an `expect`
 * hint, and prefills the matching fields: the RETURN doc fills amount + return confirmation #; the
 * PAYMENT doc fills the bank-debit fields + payment confirmation #. Extraction soft-fails silently —
 * Ok=false leaves those fields blank for manual entry and never blocks Save.
 */
const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalM },
  errorBar: { marginBottom: tokens.spacingVerticalS },
  subtitle: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  outcome: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  outcomeBad: { fontSize: tokens.fontSizeBase200, color: tokens.colorPaletteRedForeground1 },
});

const CADENCES: FilingCadence[] = ['Monthly', 'Quarterly', 'SemiAnnual', 'Annual'];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stateTaxClientId: string;
  obligations: StateTaxObligationSummary[];
  defaultTaxType?: string;
  defaultPeriodStart?: string;
  recordedByName?: string;
  /**
   * Client's display name (Dataverse account name), used to render the Flow C
   * name-mismatch warning ("document reads X, doesn't match client Y"). Optional so
   * existing/legacy call sites and tests that don't thread it keep compiling — omitted,
   * the warning falls back to an empty client name rather than failing to render.
   */
  clientName?: string;
  /** Called after a successful save, in addition to closing the dialog. */
  onSaved?: () => void;
}

interface FormState {
  taxType: string;
  cadence: FilingCadence;
  periodStart: string;
  filedOn: string;
  returnConfirmationNumber: string;
  paymentConfirmationNumber: string;
  amount: string;
  bankDebitAmount: string;
  bankDebitDate: string;
  bankAccountMasked: string;
  note: string;
}

// Which slot is mid-extraction (drives the per-slot spinner). null = idle.
type Extracting = 'return' | 'payment' | null;

// What became of the document on each slot, said under it (the primitive's per-file outcome). The
// figures it read only fill blank fields, so "read" never means "overwrote what you typed".
type SlotOutcome = 'reading' | 'read' | 'unread';

// Map a Dataverse frequency label to the FilingCadence enum used by the grid math.
function cadenceFromFrequency(label?: string): FilingCadence {
  switch ((label ?? '').toLowerCase()) {
    case 'quarterly': return 'Quarterly';
    case 'semi-annually': case 'semi annually': case 'semiannually': return 'SemiAnnual';
    case 'annually': case 'annual': return 'Annual';
    default: return 'Monthly';
  }
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// First day of the previous calendar month — the default period for a standalone manual filing
// (you almost always record the period that just closed, not the current in-progress one).
function lastCompletedMonthStart(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// Human label for a period start + cadence, mirroring the server's LabelForCadencePeriod but
// using the full localized month name (e.g. "March 2026" / "marzo de 2026"; "Q1 2026"; "2026").
function periodDisplayLabel(periodStartIso: string, cadence: FilingCadence, lang: string): string {
  const [y, m] = periodStartIso.split('-').map(Number);
  const monthIdx = (m || 1) - 1;
  switch (cadence) {
    case 'Quarterly': return `Q${Math.floor(monthIdx / 3) + 1} ${y}`;
    case 'SemiAnnual': return `H${Math.floor(monthIdx / 6) + 1} ${y}`;
    case 'Annual': return String(y);
    default: return new Date(y, monthIdx, 1).toLocaleDateString(lang || 'en-US', { month: 'long', year: 'numeric' });
  }
}

function parseDecimal(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

const numStr = (n?: number | null): string => (n === undefined || n === null ? '' : String(n));

export default function RecordManualFilingDialog({
  open,
  onOpenChange,
  stateTaxClientId,
  obligations,
  defaultTaxType,
  defaultPeriodStart,
  recordedByName,
  clientName,
  onSaved,
}: Props) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const { accounts } = useMsal();

  // When launched from a period cell the period is already determined → show it read-only.
  // Standalone (the activity-card button) → a month picker defaulting to the last closed period.
  const periodKnown = defaultPeriodStart != null;

  const initialType = defaultTaxType ?? obligations[0]?.taxType ?? '';
  const initialCadence = cadenceFromFrequency(
    obligations.find((o) => o.taxType === initialType)?.filingFrequencyLabel,
  );

  const emptyForm = (): FormState => ({
    taxType: initialType,
    cadence: initialCadence,
    periodStart: defaultPeriodStart ?? lastCompletedMonthStart(),
    filedOn: todayIso(),
    returnConfirmationNumber: '',
    paymentConfirmationNumber: '',
    amount: '',
    bankDebitAmount: '',
    bankDebitDate: '',
    bankAccountMasked: '',
    note: '',
  });

  const [form, setForm] = useState<FormState>(emptyForm);
  const formRef = useRef(form);
  formRef.current = form;
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [returnFile, setReturnFile] = useState<File | null>(null);
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [extracting, setExtracting] = useState<Extracting>(null);
  const [outcomes, setOutcomes] = useState<Partial<Record<FilingDocumentKind, SlotOutcome>>>({});
  // Files refused before anything was sent (wrong type, empty, too large, or a drop that can't be placed).
  const [refusal, setRefusal] = useState<string | null>(null);
  // Flow C name-mismatch guard — this dialog handles exactly one filing per open session
  // (unlike Flow A's per-obligation task rows), so it's a single nullable entry rather
  // than a Map. Set when either the return or payment extraction comes back with
  // businessNameMatch === false; a subsequent successful extraction with a matching (or
  // absent) businessNameMatch clears it (fail-open).
  const [nameGuard, setNameGuard] = useState<ManualFilingNameGuard | null>(null);
  // The ack built when the staffer confirms the override, persisted separately from
  // `nameGuard.acknowledged`. If `doSubmit` throws (transient failure, dialog stays open),
  // a Save retry must still send this same ack — without it, a retry after a failed
  // override submit would fall through the (now-satisfied) gate and submit with NO ack,
  // losing the override audit trail on exactly the path this feature protects.
  const [acknowledgedAck, setAcknowledgedAck] = useState<NameMismatchAck | null>(null);
  // Save-click confirm-to-override dialog — mirrors Flow A's markFiledConfirmTarget gate.
  const [confirmOverrideOpen, setConfirmOverrideOpen] = useState(false);

  const record = useRecordManualFiling();

  // Whether the selected obligation's form has a SEPARATE payment-confirmation report (sales tax,
  // solid waste). Drives the second upload slot + the payment-only fields. Combined forms
  // (doc stamp, CST) show the return amount + return confirmation # only.
  const hasPaymentConfirmation =
    obligations.find((o) => o.taxType === form.taxType)?.hasPaymentConfirmation ?? false;

  useEffect(() => {
    if (open) {
      setForm(emptyForm());
      setErrors({});
      setSubmitError(null);
      setReturnFile(null);
      setPaymentFile(null);
      setFileInputKey((k) => k + 1);
      setExtracting(null);
      setOutcomes({});
      setRefusal(null);
      setNameGuard(null);
      setAcknowledgedAck(null);
      setConfirmOverrideOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultPeriodStart]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  // Flow C name-mismatch guard update, shared by both extraction handlers. Only trusted
  // when the extraction itself succeeded (mirrors Flow A's UploadExtractControl.onExtracted,
  // which only inspects businessNameMatch after its own `!r.ok` early-return). STRICT
  // `=== false` check — fail-open means undefined/true never sets or re-sets the guard, and
  // a fresh non-mismatched extraction on either slot clears a stale warning from the other.
  function applyNameGuardFromExtract(result: { ok: boolean; businessNameMatch?: boolean; extractedBusinessName?: string | null; businessNameScore?: number | null; matchedAgainst?: 'legal' | 'dba' | null }) {
    if (!result.ok) return;
    if (result.businessNameMatch === false) {
      setNameGuard({
        show: true,
        extractedName: result.extractedBusinessName,
        score: result.businessNameScore,
        matchedAgainst: result.matchedAgainst,
        acknowledged: false,
      });
    } else {
      setNameGuard(null);
    }
  }

  // Read taxType from the ref to avoid a stale closure — the user may have changed the obligation
  // dropdown after selecting the file. Only fill blank fields so prior user edits aren't clobbered.
  async function handleReturnFile(selected: File | null) {
    setReturnFile(selected);
    if (!selected) return;
    setExtracting('return');
    setOutcome('return', 'reading');
    try {
      const result = await extractManualFiling(stateTaxClientId, formRef.current.taxType, selected, 'return');
      setOutcome('return', result.ok ? 'read' : 'unread');
      if (result.ok && result.extract) {
        const ex = result.extract;
        const totalDue = filedAmounts(ex).totalDue;   // the return's own figures, never a draft (ruling 2026-10-26)
        setForm((prev) => ({
          ...prev,
          amount: prev.amount || numStr(totalDue),
          returnConfirmationNumber: prev.returnConfirmationNumber || (ex.confirmationNumber ?? ''),
        }));
      }
      applyNameGuardFromExtract(result);
    } catch {
      // An extraction error never blocks manual entry; the slot says it wasn't read.
      setOutcome('return', 'unread');
    } finally {
      setExtracting(null);
    }
  }

  async function handlePaymentFile(selected: File | null) {
    setPaymentFile(selected);
    if (!selected) return;
    setExtracting('payment');
    setOutcome('payment-confirmation', 'reading');
    try {
      const result = await extractManualFiling(stateTaxClientId, formRef.current.taxType, selected, 'payment');
      setOutcome('payment-confirmation', result.ok ? 'read' : 'unread');
      if (result.ok && result.extract) {
        const ex = result.extract;
        setForm((prev) => ({
          ...prev,
          bankDebitAmount: prev.bankDebitAmount || numStr(ex.amountPaid),
          bankDebitDate: prev.bankDebitDate || (ex.debitDate?.slice(0, 10) ?? ''),
          bankAccountMasked: prev.bankAccountMasked || (ex.bankAccountMasked ?? ''),
          paymentConfirmationNumber: prev.paymentConfirmationNumber || (ex.confirmationNumber ?? ''),
        }));
      }
      applyNameGuardFromExtract(result);
    } catch {
      setOutcome('payment-confirmation', 'unread');
    } finally {
      setExtracting(null);
    }
  }

  function setOutcome(kind: FilingDocumentKind, outcome: SlotOutcome) {
    setOutcomes((prev) => ({ ...prev, [kind]: outcome }));
  }

  // The slots this obligation's form has: the return, plus the payment confirmation when it arrives separately.
  const offeredKinds: FilingDocumentKind[] = hasPaymentConfirmation ? ['return', 'payment-confirmation'] : ['return'];

  // Dropped or picked files for one slot (the fleet upload primitive, the same rules as the Reconcile slots).
  // Each file is validated before anything is sent; several dropped together go each to the slot its filename
  // names, or none is used. The files of one drop are read one after another; a slot that is reading takes no drop.
  function handleSlotFiles(slot: FilingDocumentKind, files: File[]): boolean {
    const { placed, refused } = routeDrop(files, slot, offeredKinds);
    setRefusal(refused.length > 0 ? refused.join(' ') : null);
    void (async () => {
      for (const p of placed) {
        if (p.kind === 'return') await handleReturnFile(p.file);
        else await handlePaymentFile(p.file);
      }
    })();
    return placed.some((p) => p.kind === slot);
  }

  function slotOutcome(kind: FilingDocumentKind) {
    const o = outcomes[kind];
    if (!o) return null;
    return (
      <Text className={o === 'unread' ? styles.outcomeBad : styles.outcome} data-testid={`manual-filing-outcome-${kind}`}>
        {t(`recordManualFiling.documents.${o}`)}
      </Text>
    );
  }

  // An upload that fails at Save names its file, so the person knows which one to look at.
  async function uploadNamed(file: File) {
    try {
      return await uploadManualFilingAttachment(stateTaxClientId, file);
    } catch (err) {
      throw new Error(t('recordManualFiling.documents.uploadFailed', {
        name: file.name,
        error: err instanceof Error ? err.message : t('recordManualFiling.errors.unexpected'),
      }));
    }
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.taxType.trim()) next.taxType = t('recordManualFiling.validation.obligationRequired');
    if (!form.periodStart.trim()) next.periodStart = t('recordManualFiling.validation.periodRequired');
    if (!form.filedOn.trim()) next.filedOn = t('recordManualFiling.validation.filedOnRequired');
    if (form.amount.trim() && parseDecimal(form.amount) === undefined) {
      next.amount = t('recordManualFiling.validation.amountNumber');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  // Save-click entry point: validates, then — same as Flow A's Mark-Filed gate — intercepts
  // when there's an unacknowledged name-mismatch guard and opens the confirm-to-override
  // dialog instead of saving directly. `doSubmit` does the actual save, called either
  // straight from here (no gate) or from the confirm dialog's override button (with `ack`).
  //
  // When the guard is present AND already acknowledged (e.g. a Save retry after a failed
  // override submit — doSubmit threw, the dialog stayed open, `nameGuard.acknowledged` is
  // still true from the first attempt), the gate is satisfied so this falls straight to
  // doSubmit — but it must still pass the PERSISTED ack, not undefined, or the retry loses
  // the override audit trail on exactly the path this feature protects.
  function handleSaveClick() {
    if (!validate()) return;
    if (shouldGateManualFilingSave(nameGuard)) {
      setConfirmOverrideOpen(true);
      return;
    }
    void doSubmit(resolveManualFilingSaveAck(nameGuard, acknowledgedAck));
  }

  async function doSubmit(ack?: NameMismatchAck) {
    setSubmitError(null);
    try {
      // Upload each present PDF first (if any) so the saved filing references the blobs. If an
      // upload fails we bail before creating a half-complete record.
      let returnAtt: { blobName: string; fileName: string; contentType: string } | undefined;
      let paymentAtt: { blobName: string; fileName: string; contentType: string } | undefined;
      if (returnFile) {
        returnAtt = await uploadNamed(returnFile);
      }
      if (paymentFile && hasPaymentConfirmation) {
        paymentAtt = await uploadNamed(paymentFile);
      }

      await record.mutateAsync({
        stateTaxClientId,
        payload: {
          taxType: form.taxType.trim(),
          periodStart: form.periodStart,
          cadence: form.cadence,
          filedOn: form.filedOn,
          returnConfirmationNumber: form.returnConfirmationNumber.trim() || undefined,
          paymentConfirmationNumber: hasPaymentConfirmation
            ? form.paymentConfirmationNumber.trim() || undefined
            : undefined,
          amount: parseDecimal(form.amount),
          note: form.note.trim() || undefined,
          attachmentBlobName: returnAtt?.blobName,
          attachmentFileName: returnAtt?.fileName,
          attachmentContentType: returnAtt?.contentType,
          paymentAttachmentBlobName: paymentAtt?.blobName,
          paymentAttachmentFileName: paymentAtt?.fileName,
          paymentAttachmentContentType: paymentAtt?.contentType,
          recordedByName: recordedByName?.trim() || undefined,
          bankDebitAmount: hasPaymentConfirmation ? parseDecimal(form.bankDebitAmount) : undefined,
          bankDebitDate: hasPaymentConfirmation ? form.bankDebitDate.trim() || undefined : undefined,
          bankAccountMasked: hasPaymentConfirmation ? form.bankAccountMasked.trim() || undefined : undefined,
          nameMismatchAck: ack,
        },
      });
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('recordManualFiling.errors.unexpected'));
    }
  }

  const isPending = record.isPending || extracting !== null;

  return (
    <>
    <Dialog open={open} onOpenChange={(_e, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('recordManualFiling.title')}</DialogTitle>
          <DialogContent>
            <Text className={styles.subtitle} block style={{ marginBottom: tokens.spacingVerticalM }}>
              {t('recordManualFiling.subtitle')}
            </Text>

            {submitError && (
              <MessageBar intent="error" className={styles.errorBar}>
                <MessageBarBody>{submitError}</MessageBarBody>
              </MessageBar>
            )}

            <div className={styles.form}>
              <Field label={t('recordManualFiling.fields.obligation')} required validationMessage={errors.taxType}>
                <Dropdown
                  value={form.taxType}
                  selectedOptions={[form.taxType]}
                  onOptionSelect={(_e, data) => {
                    const taxType = data.optionValue ?? '';
                    setField('taxType', taxType);
                    // Default the cadence to the obligation's configured frequency.
                    setField('cadence', cadenceFromFrequency(
                      obligations.find((o) => o.taxType === taxType)?.filingFrequencyLabel,
                    ));
                  }}
                >
                  {obligations.map((o) => (
                    <Option key={o.id} value={o.taxType}>{o.taxType}</Option>
                  ))}
                </Dropdown>
              </Field>

              <div className={styles.row}>
                <Field label={t('recordManualFiling.fields.cadence')}>
                  <Dropdown
                    value={t(`recordManualFiling.cadence.${form.cadence}`)}
                    selectedOptions={[form.cadence]}
                    onOptionSelect={(_e, data) => setField('cadence', (data.optionValue as FilingCadence) ?? 'Monthly')}
                  >
                    {CADENCES.map((c) => (
                      <Option key={c} value={c} text={t(`recordManualFiling.cadence.${c}`)}>
                        {t(`recordManualFiling.cadence.${c}`)}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label={t('recordManualFiling.fields.period')} required validationMessage={errors.periodStart}>
                  {periodKnown ? (
                    // Determined by the cell the user clicked — show it, don't ask for it.
                    <Text style={{ paddingTop: tokens.spacingVerticalXS, fontWeight: tokens.fontWeightSemibold }}>
                      {periodDisplayLabel(form.periodStart, form.cadence, i18n.language)}
                    </Text>
                  ) : (
                    <Input
                      type="month"
                      value={form.periodStart.slice(0, 7)}
                      onChange={(_e, d) => setField('periodStart', d.value ? `${d.value}-01` : '')}
                    />
                  )}
                </Field>
              </div>

              <div className={styles.row}>
                <Field label={t('recordManualFiling.fields.filedOn')} required validationMessage={errors.filedOn}>
                  <CalendarDateInput value={form.filedOn} onChange={(v) => setField('filedOn', v)} />
                </Field>
                <Field label={t('recordManualFiling.fields.amount')} validationMessage={errors.amount}>
                  <Input value={form.amount} onChange={(_e, d) => setField('amount', d.value)} />
                </Field>
              </div>

              <Field label={t('recordManualFiling.fields.returnConfirmation')}>
                <Input
                  value={form.returnConfirmationNumber}
                  onChange={(_e, d) => setField('returnConfirmationNumber', d.value)}
                />
              </Field>

              <Field label={t('recordManualFiling.fields.returnDocument')}>
                <DocumentDropSlot
                  key={`return-${fileInputKey}`}
                  testId="manual-filing-dropzone-return"
                  label={t('recordManualFiling.fields.returnDocument')}
                  busy={extracting === 'return'}
                  fileName={returnFile?.name ?? null}
                  onFiles={(files) => handleSlotFiles('return', files)}
                >
                  {slotOutcome('return')}
                </DocumentDropSlot>
              </Field>

              {refusal && (
                <MessageBar intent="warning" data-testid="manual-filing-refused">
                  <MessageBarBody>{refusal}</MessageBarBody>
                </MessageBar>
              )}

              {nameGuard?.show && (
                <NameMismatchWarning
                  show
                  extractedName={nameGuard.extractedName}
                  clientName={clientName ?? ''}
                />
              )}

              {hasPaymentConfirmation && (
                <>
                  <Field label={t('recordManualFiling.fields.paymentConfirmation')}>
                    <Input
                      value={form.paymentConfirmationNumber}
                      onChange={(_e, d) => setField('paymentConfirmationNumber', d.value)}
                    />
                  </Field>

                  <div className={styles.row}>
                    <Field label={t('recordManualFiling.fields.bankDebitAmount')}>
                      <Input value={form.bankDebitAmount} onChange={(_e, d) => setField('bankDebitAmount', d.value)} />
                    </Field>
                    <Field label={t('recordManualFiling.fields.bankDebitDate')}>
                      <CalendarDateInput value={form.bankDebitDate} onChange={(v) => setField('bankDebitDate', v)} />
                    </Field>
                  </div>

                  <Field label={t('recordManualFiling.fields.bankAccountMasked')}>
                    <Input value={form.bankAccountMasked} onChange={(_e, d) => setField('bankAccountMasked', d.value)} />
                  </Field>

                  <Field label={t('recordManualFiling.fields.paymentDocument')}>
                    <DocumentDropSlot
                      key={`payment-${fileInputKey}`}
                      testId="manual-filing-dropzone-payment-confirmation"
                      label={t('recordManualFiling.fields.paymentDocument')}
                      busy={extracting === 'payment'}
                      fileName={paymentFile?.name ?? null}
                      onFiles={(files) => handleSlotFiles('payment-confirmation', files)}
                    >
                      {slotOutcome('payment-confirmation')}
                    </DocumentDropSlot>
                  </Field>
                </>
              )}

              <Field label={t('recordManualFiling.fields.note')}>
                <Textarea value={form.note} onChange={(_e, d) => setField('note', d.value)} />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={isPending} onClick={() => onOpenChange(false)}>
              {t('recordManualFiling.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabled={isPending}
              icon={isPending ? <Spinner size="tiny" /> : undefined}
              onClick={handleSaveClick}
            >
              {t('recordManualFiling.submit')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    {/* Flow C confirm-to-override — same shared nameGuard i18n keys as Flow A's Mark-Filed
        gate. A sibling Dialog, NOT nested inside the outer one — Fluent's Dialog expects
        exactly a [DialogTrigger?, DialogSurface] child list, so a second Dialog nested
        inside it would get misparsed as trigger/content. Its portal content doesn't mount
        while `open` is false, so it stays inert until a gated Save click opens it. */}
      <Dialog
        open={confirmOverrideOpen}
        onOpenChange={(_e, d) => { if (!d.open) setConfirmOverrideOpen(false); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('clientDetailPage.nameGuard.confirmTitle')}</DialogTitle>
            <DialogContent>
              {t('clientDetailPage.nameGuard.confirmBody', {
                extracted: nameGuard?.extractedName ?? '',
                client: clientName ?? '',
              })}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmOverrideOpen(false)}>
                {t('clientDetailPage.nameGuard.cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  const entry = nameGuard;
                  setConfirmOverrideOpen(false);
                  if (!entry) return;
                  const ack: NameMismatchAck = {
                    acknowledgedByName: accounts[0]?.name ?? accounts[0]?.username ?? null,
                    acknowledgedAt: new Date().toISOString(),
                    extractedName: entry.extractedName,
                    score: entry.score,
                    matchedAgainst: entry.matchedAgainst,
                  };
                  setNameGuard({ ...entry, acknowledged: true });
                  setAcknowledgedAck(ack);
                  void doSubmit(ack);
                }}
              >
                {t('clientDetailPage.nameGuard.confirmOverride')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
